// Content for the help center (Pages/Help/Help.tsx). Add a question by adding an entry to helpArticles -
// no JSX needed. "id" is used as the url hash (/help#id), so keep it stable once published.
// Swedish only for now; the page shows the same content regardless of language.
// Wrap a menu path in double brackets, e.g. "Gå till [[Inställningar > Medlemsformulär]].", to have it
// rendered as a label. Works in intro, steps and notes.

import { ComponentType, CSSProperties } from 'react';
import { CalendarOutlined, CreditCardOutlined, FolderOpenOutlined, GlobalOutlined, MailOutlined, PictureOutlined, RocketOutlined, TeamOutlined } from '@ant-design/icons';

export type HelpSection = {
    id: string;
    title: string;
    description: string;
    icon: ComponentType<{ className?: string; style?: CSSProperties }>;
};

export type HelpLink = {
    label: string;
    /** Admin route without language prefix, e.g. "memberFormSettings". Only shown when logged in. */
    path: string;
};

export type HelpArticle = {
    id: string;
    /** Rough time to follow the guide, shown as "ca N min". */
    minutes?: number;
    sectionId: string;
    question: string;
    /** Short answer shown before the steps. */
    intro: string;
    steps?: string[];
    /** Shown after the steps, for limitations and tips. */
    notes?: string[];
    links?: HelpLink[];
    /** Extra search words that don't appear in the question itself. */
    keywords?: string[];
};

export const helpSections: HelpSection[] = [
    { id: "getting-started", title: "Kom igång", description: "Importera medlemmar och bjud in styrelsen.", icon: RocketOutlined },
    { id: "members", title: "Medlemmar", description: "Fält, uppgifter och medlemsregistret.", icon: TeamOutlined },
    { id: "payments", title: "Betalningar", description: "Avgifter, betallänkar och påminnelser.", icon: CreditCardOutlined },
    { id: "website", title: "Hemsida och medlemssida", description: "Bli medlem-formulär och Mina sidor.", icon: GlobalOutlined },
    { id: "communication", title: "Utskick", description: "E-post, mallar och automatiska utskick.", icon: MailOutlined },
    { id: "events", title: "Evenemang", description: "Inbjudningar, svar och påminnelser.", icon: CalendarOutlined },
    { id: "files", title: "Fillagring", description: "Spara styrelsens dokument på ett ställe.", icon: FolderOpenOutlined },
    { id: "images", title: "Bildbank", description: "Bilder till utskick, mallar och event.", icon: PictureOutlined },
];

export const helpArticles: HelpArticle[] = [
    {
        id: "importera-medlemmar-excel",
        minutes: 10,
        sectionId: "getting-started",
        question: "Jag har mina medlemmar i Excel, hur får jag in dem?",
        intro: "Du kan importera hela medlemsregistret från en Excel- eller csv-fil. Enklast är att utgå från vår exempelmall så att kolumnerna hamnar rätt.",
        steps: [
            "Gå till Medlemmar, klicka på knappen \"Import/Export\" ovanför listan och välj Medlemmar.",
            "Klicka på \"Ladda ner mall\" och klistra in era medlemmar i mallens kolumner.",
            "Klicka på \"Välj fil från dator\" och välj filen. Du får en förhandsgranskning innan något sparas.",
            "Kontrollera förhandsgranskningen. Rader med fel är markerade, och överst ser du hur många som hoppas över och varför.",
            "Klicka på \"Lägg till ... medlemmar\" för att genomföra importen.",
        ],
        notes: [
            "Rader hoppas över om e-postadressen är ogiltig, eller om samma e-post eller medlems-ID finns flera gånger i filen eller redan finns i systemet. Rätta dem i filen och ladda upp igen - du kan använda importen flera gånger.",
            "Familjemedlemmar importeras genom att ange huvudmedlemmens medlemsnummer i kolumnen HuvudmedlemsNr.",
            "Mallen går bara att ladda ner med ett betalabonnemang, och i gratisversionen går det att ha högst 25 medlemmar.",
        ],
        links: [
            { label: "Öppna import av medlemmar", path: "uploadmembers" },
        ],
        keywords: ["import", "importera", "csv", "xlsx", "fil", "ladda upp", "mall", "register", "flytta", "byta system", "kalkylark"],
    },
    {
        id: "fler-administratorer",
        minutes: 2,
        sectionId: "getting-started",
        question: "Jag vill att fler i styrelsen ska kunna logga in, hur gör jag?",
        intro: "Bjud in dem som administratörer. Var och en loggar in med sin egen e-postadress, och du väljer vilken behörighet personen ska ha.",
        steps: [
            "Gå till [[Inställningar > Administratörer]].",
            "Klicka på \"Lägg till administratör\".",
            "Ange personens e-postadress och välj behörighet.",
            "Personen får en inbjudan via e-post och skapar själv sitt lösenord.",
        ],
        notes: [
            "Fullständig behörighet ger tillgång till allt, även inställningar, betalning och andra administratörer. Redigera behörighet räcker för den som ska arbeta med medlemmar, utskick och evenemang. Läs behörighet ger bara rätt att titta.",
            "Hur många administratörer ni kan ha beror på ert abonnemang. Högst två kan ha fullständig behörighet.",
            "Kommer inbjudan inte fram? Be personen titta i skräpposten.",
        ],
        links: [
            { label: "Öppna Administratörer", path: "admins" },
        ],
        keywords: ["admin", "administratör", "användare", "bjuda in", "kassör", "ordförande", "sekreterare", "kollega", "inloggning", "behörighet", "rättigheter"],
    },
    {
        id: "bara-lasrattigheter",
        minutes: 2,
        sectionId: "getting-started",
        question: "Kan någon få bara läsrättigheter?",
        intro: "Ja. Välj behörigheten \"Läs behörighet\" när du lägger till administratören. Personen kan då se medlemsregistret men inte ändra något.",
        steps: [
            "Gå till [[Inställningar > Administratörer]].",
            "Klicka på \"Lägg till administratör\", ange e-postadressen och välj \"Läs behörighet\".",
            "Har personen redan ett konto? Klicka på pennan vid namnet och byt behörighet där.",
        ],
        notes: [
            "En administratör med läsbehörighet ser inte menyerna för evenemang och kommunikation, och kan alltså inte skicka utskick.",
            "Antalet administratörer med läsbehörighet räknas separat från dem som får redigera, och gränsen beror på ert abonnemang.",
        ],
        links: [
            { label: "Öppna Administratörer", path: "admins" },
        ],
        keywords: ["läsa", "läsbehörighet", "titta", "revisor", "valberedning", "behörighet", "rättigheter", "roll", "admin"],
    },
    {
        id: "medlemmar-utan-epost",
        minutes: 2,
        sectionId: "members",
        question: "E-post är obligatorisk, men alla mina medlemmar har inte e-post. Hur gör jag?",
        intro: "E-post behöver inte vara obligatorisk när du som administratör lägger upp medlemmar. Du väljer själv vilka fält som måste fyllas i.",
        steps: [
            "Gå till [[Inställningar > Medlemsformulär]].",
            "Leta upp raden E-post.",
            "Avmarkera \"Obligatorisk admin\". Då kan du lägga upp och spara medlemmar utan e-postadress.",
            "Klicka på Spara.",
        ],
        notes: [
            "Den som registrerar sig själv via er medlemssida behöver alltid en e-postadress, eftersom kontot aktiveras via en länk som skickas dit. Medlemmar utan e-post lägger ni alltså upp själva.",
            "Medlemmar utan e-post får inga e-postutskick. Du når dem i stället med sms (kryssa i \"Skicka till medlemmar som saknar e-postadress\") eller med brev: under Medlemmar > Import/Export > Medlemmar kan du ladda ner adressetiketter för \"Endast utan e-post\". Etiketterna kräver ett betalabonnemang.",
            "En medlem utan e-postadress kan inte logga in på medlemssidan, eftersom inloggningslänken skickas med e-post. Deras uppgifter behöver ni ändra åt dem.",
        ],
        links: [
            { label: "Öppna Medlemsformulär", path: "memberFormSettings" },
            { label: "Skicka sms", path: "sendSms" },
        ],
        keywords: ["mejl", "mail", "email", "saknar", "obligatoriskt", "fält", "brev", "etiketter"],
    },
    {
        id: "egna-medlemsfalt",
        minutes: 5,
        sectionId: "members",
        question: "Jag vill spara mer om mina medlemmar än det som finns, hur skapar jag egna fält?",
        intro: "Lägg till ett eget fält, ett så kallat specialfält, i medlemsformuläret. Det kan till exempel vara båtnamn, tröjstorlek eller vilket år medlemmen gick med.",
        steps: [
            "Gå till [[Inställningar > Medlemsformulär]].",
            "Klicka på \"Lägg till specialfält\" under listan med fält.",
            "Ge fältet ett namn och välj typ i UI-kontroll, till exempel Textfält, Datumväljare, Kryssruta eller Dropdown.",
            "Välj Dropdown om medlemmen ska välja bland färdiga svar, och skriv svaren i Alternativ, åtskilda med semikolon (t.ex. \"S;M;L;XL\").",
            "Kryssa i var fältet ska synas och om det ska vara obligatoriskt: \"admin\" gäller när ni själva lägger upp medlemmar, \"medlem\" gäller formulären på er medlemssida.",
            "Klicka på \"Lägg till\". Fältet finns nu på alla medlemmar.",
        ],
        notes: [
            "Fält som redan finns i systemet men inte visas, till exempel personnummer eller telefon, lägger du till med \"Lägg till fält\" i stället.",
            "Ska fältet hämta sina värden från en egen datatabell väljer du UI-kontroll Uppslag, och väljer sedan tabellen i kolumnen Kopplingstabell i medlemsformuläret. Klicka på Spara när du är klar.",
            "Specialfält kräver abonnemanget Premium eller högre.",
        ],
        links: [
            { label: "Öppna Medlemsformulär", path: "memberFormSettings" },
        ],
        keywords: ["skapa", "egna", "eget", "medlemsfält", "specialfält", "fält", "lägga till fält", "anpassa", "extra", "kolumn", "uppgift", "information", "dropdown"],
    },
    {
        id: "egna-datatabeller",
        minutes: 10,
        sectionId: "members",
        question: "Hur skapar jag en egen datatabell, t.ex. för båtplatser eller utrustning?",
        intro: "En egen datatabell är ett eget register med de fält ni själva bestämmer. Använd det för information som inte hör till en enskild medlem, eller där en medlem kan ha flera rader, till exempel båtplatser, nycklar eller utrustning.",
        steps: [
            "Gå till [[Inställningar > Egna datatabeller]].",
            "Skriv ett namn på tabellen i Listnamn och klicka på \"Skapa lista\".",
            "Klicka på Öppna vid tabellen och sedan på \"Lägg till fält\" för varje kolumn tabellen ska ha.",
            "Klicka på \"Spara ändringar\".",
            "Gå till Medlemmar. Tabellen finns nu i menyn till vänster under Egna datatabeller, och där lägger du till rader och kopplar dem till medlemmar.",
        ],
        notes: [
            "Kryssa i \"Tillåt medlemmar att kunna redigera\" när du skapar tabellen om medlemmarna själva ska kunna se och ändra sina egna rader på medlemssidan.",
            "Vill du välja en rad i tabellen direkt på medlemmen? Skapa ett specialfält med UI-kontroll Uppslag och koppla det till tabellen, se guiden om egna medlemsfält.",
            "Ändringar i tabellerna loggas och syns under Historik i samma meny.",
            "Egna datatabeller kräver abonnemanget Premium eller högre.",
        ],
        links: [
            { label: "Öppna Egna datatabeller", path: "customListsSettings" },
        ],
        keywords: ["skapa", "egna", "egen", "datatabell", "datatabeller", "tabell", "lista", "register", "båtplats", "nycklar", "utrustning", "lån", "inventarier", "koppla"],
    },
    {
        id: "ta-betalt-kom-igang",
        minutes: 15,
        sectionId: "payments",
        question: "Jag vill ta betalt av mina medlemmar, hur kommer jag igång?",
        intro: "Du slår först på betalningar för organisationen, lägger upp vilka avgifter ni har, kopplar dem till medlemmarna och väljer sedan hur betalningen ska gå till.",
        steps: [
            "Gå till [[Inställningar > Konto]] och välj \"Helårs betalningar\" i fältet Betalningar under Organisationsinställningar. Spara.",
            "Gå till [[Inställningar > Medlems-/tilläggsavgifter]] och skapa era avgifter, till exempel \"Medlemsavgift vuxen\".",
            "Koppla rätt avgift till varje medlem i fältet Medlemskap på medlemmen.",
            "Välj betalsätt under [[Inställningar > Integrationer]]: PingPayments för betallänkar som medlemmen betalar direkt, eller Fortnox/Spiris om ni vill skicka fakturor från ert bokföringssystem.",
            "Gå till Medlemmar och klicka på \"Skapa betallänkar\", \"Skapa Fortnox-fakturor\" eller \"Skapa Spiris-fakturor\". Knapparna visas när respektive integration är aktiv.",
            "Följ upp vilka som har betalat med filtret Betalstatus i medlemslistan och skicka påminnelse till dem som inte gjort det.",
        ],
        notes: [
            "Ni kan också sköta betalningarna helt manuellt: öppna Betalningar på medlemmen och klicka på \"Lägg till manuell betalning\".",
        ],
        links: [
            { label: "Öppna Medlems-/tilläggsavgifter", path: "membershipproducts" },
            { label: "Öppna Integrationer", path: "integrations" },
        ],
        keywords: ["betala", "betalning", "medlemsavgift", "avgift", "faktura", "fortnox", "spiris", "pingpayments", "ping", "swish", "betallänk", "inbetalning"],
    },
    {
        id: "olika-medlemsavgifter",
        minutes: 5,
        sectionId: "payments",
        question: "Jag har olika medlemsavgifter, hur gör jag?",
        intro: "Lägg upp en avgift för varje nivå ni har, till exempel vuxen, junior och familj. Sedan kopplar du varje medlem till den avgift som gäller för just den medlemmen.",
        steps: [
            "Gå till [[Inställningar > Medlems-/tilläggsavgifter]].",
            "Klicka på \"Lägg till ny Medlems-/tilläggsavgift\". Välj Medlemsavgift som typ av avgift och ange namn och pris. Gör en per nivå.",
            "Har ni avgifter som betalas utöver medlemsavgiften, till exempel båtplats eller träningsavgift? Skapa dem med typen Tilläggsavgift.",
            "Kryssa i \"Valbar som medlem vid nyregistrering\" på de avgifter som nya medlemmar själva ska kunna välja i registreringsformuläret.",
            "Öppna en medlem och välj rätt avgift i fältet Medlemskap. Tilläggsavgifterna visas som kryssrutor direkt under samma fält.",
        ],
        notes: [
            "Betallänkar och fakturor skapas utifrån den avgift som är kopplad till medlemmen. Medlemmar utan kopplad avgift hoppas över när ni skapar betallänkar.",
        ],
        links: [
            { label: "Öppna Medlems-/tilläggsavgifter", path: "membershipproducts" },
            { label: "Öppna Medlemmar", path: "users" },
        ],
        keywords: ["betala", "betalning", "avgifter", "pris", "junior", "senior", "familj", "nivå", "tillägg", "tilläggsavgift", "medlemskap", "produkt"],
    },
    {
        id: "ta-betalt-vid-registrering",
        minutes: 10,
        sectionId: "payments",
        question: "När en ny medlem registrerar sig vill jag ta betalt, hur gör jag?",
        intro: "Lägg en betallänk i välkomstmailet. När den nya medlemmen har aktiverat sitt konto skapas en personlig betallänk utifrån den avgift medlemmen valde, och den skickas med i mailet.",
        steps: [
            "Aktivera PingPayments under [[Inställningar > Integrationer]]. Betallänkar kräver den integrationen.",
            "Gå till [[Inställningar > Medlems-/tilläggsavgifter]] och kryssa i \"Valbar som medlem vid nyregistrering\" på de avgifter som nya medlemmar ska kunna välja.",
            "Gå till [[Inställningar > Medlemsformulär]] och kryssa i \"Synlig medlem\" på raden Medlemskap (avgift), så att avgiften går att välja i registreringsformuläret.",
            "Gå till [[Inställningar > Epostmallar]], öppna fliken Automatiska utskick och klicka på \"Skapa automatiskt utskick\". Välj händelsen \"Välkomstmail till ny medlem som registrerat sig\".",
            "Infoga variabeln \"betallänk\" i texten där länken ska visas, och spara.",
        ],
        notes: [
            "Betallänken skapas bara om medlemmen har valt en avgift med ett pris. Annars skickas välkomstmailet utan länk.",
            "Tills betalningen är gjord har medlemmen statusen \"Obekräftad första betalning\", så du ser enkelt vilka som inte har betalat.",
            "Använder ni Fortnox eller Spiris i stället skapar ni fakturan till den nya medlemmen i efterhand, från Medlemmar.",
        ],
        links: [
            { label: "Öppna Integrationer", path: "integrations" },
            { label: "Öppna Medlems-/tilläggsavgifter", path: "membershipproducts" },
            { label: "Öppna Epostmallar", path: "emailTemplatesAndAutomation" },
        ],
        keywords: ["betala", "betalning", "nyregistrering", "bli medlem", "betallänk", "pingpayments", "ping", "swish", "välkomstmail", "direkt", "automatiskt"],
    },
    {
        id: "vilka-har-inte-betalat",
        minutes: 1,
        sectionId: "payments",
        question: "Hur ser jag vilka som inte har betalat årets avgift?",
        intro: "Filtrera medlemslistan på betalstatus. Då visas bara de medlemmar som inte har betalat.",
        steps: [
            "Gå till Medlemmar.",
            "Välj \"Obetald\" i filtret Betalstatus ovanför listan, och kontrollera att rätt år är valt i rutan bredvid.",
            "Kombinera gärna med filtren Grupp eller Medlemsavgift om du bara vill se en viss del av medlemmarna.",
        ],
        notes: [
            "Ser du inget filter för Betalstatus? Gå till [[Inställningar > Konto]] och välj \"Helårs betalningar\" i fältet Betalningar.",
            "Byt år i rutan bredvid filtret för att se vilka som inte betalade förra året.",
            "Betalningar som kommit in på annat sätt, till exempel kontant eller direkt till ert konto, registrerar du på medlemmen med \"Lägg till manuell betalning\". Annars står medlemmen kvar som obetald.",
        ],
        links: [
            { label: "Öppna Medlemmar", path: "users" },
        ],
        keywords: ["obetald", "obetalda", "betalstatus", "skyldig", "saknas", "filter", "lista", "årsavgift", "medlemsavgift", "betalt"],
    },
    {
        id: "paminnelse-obetalda",
        minutes: 5,
        sectionId: "payments",
        question: "Hur skickar jag en påminnelse till dem som inte har betalat?",
        intro: "Skicka ett vanligt e-postutskick och välj att det bara ska gå till medlemmar med betalstatus Obetald.",
        steps: [
            "Gå till [[Kommunikation > Skicka e-post > Grupp/kategori]].",
            "Välj grupper i steg 1, eller lämna fältet tomt för alla medlemmar, och sätt Betalstatus till \"Obetald\".",
            "Klicka på \"Skapa sändlista\" och kontrollera listan över mottagare.",
            "Skriv din påminnelse i steg 2. Använder ni PingPayments kan du infoga variabeln \"betallänk\" så får varje medlem sin egen länk att betala med.",
            "Testskicka till dig själv och klicka sedan på \"Skicka e-post\".",
        ],
        notes: [
            "Mottagare som saknar betallänk får mailet utan länk. Du får en varning innan utskicket går iväg, så skapa betallänkarna först om det gäller många.",
            "Medlemmar utan e-post når du med sms, där samma filter på betalstatus finns.",
            "Lägg gärna upp påminnelsen som e-postmall under [[Inställningar > Epostmallar]], så kan du välja den med \"Utgå från e-postmall\" nästa år.",
            "Filtret Betalstatus visas bara om ni har valt \"Helårs betalningar\" under [[Inställningar > Konto]].",
        ],
        links: [
            { label: "Skicka e-post", path: "sendEmail" },
            { label: "Skicka sms", path: "sendSms" },
        ],
        keywords: ["påminnelse", "påminna", "obetald", "obetalda", "betallänk", "kräva", "sen betalning", "årsavgift", "medlemsavgift"],
    },
    {
        id: "koppla-till-hemsida",
        minutes: 10,
        sectionId: "website",
        question: "Jag vill koppla OneFront till min hemsida, hur gör jag?",
        intro: "Ni får en egen medlemssida hos OneFront 360 med två länkar som ni lägger in på er hemsida: en för att bli medlem och en där befintliga medlemmar loggar in och ändrar sina uppgifter.",
        steps: [
            "Gå till [[Inställningar > Din organisations OneFront site]].",
            "Fyll i Rubrik och Url för sidan och klicka på Skapa.",
            "Ladda upp er logotyp och skriv en beskrivning till medlemmarna, så att de känner igen sig.",
            "Lägg till medlemsvillkor om nya medlemmar ska godkänna något vid registrering.",
            "Kopiera de två länkarna som visas överst på sidan (\"Medlem kan registrera sig som medlem\" och \"Medlem kan redigera sina kontaktuppgifter\").",
            "Lägg in länkarna på er hemsida, till exempel som knapparna \"Bli medlem\" och \"Mina sidor\".",
        ],
        notes: [
            "Vilka fält som visas i formulären styr du under [[Inställningar > Medlemsformulär]].",
            "Medlemssök är avstängt som standard. Vill ni att medlemmar ska kunna söka bland varandra skapar du en vy av typen \"OneFront site medlemssök\" under [[Inställningar > Medlemsformulär]], i avsnittet Vyer.",
        ],
        links: [
            { label: "Öppna OneFront site", path: "publicsite" },
            { label: "Öppna Medlemsformulär", path: "memberFormSettings" },
        ],
        keywords: ["webbsida", "webbplats", "hemsidan", "länk", "integrera", "bli medlem", "registrering", "formulär", "mina sidor", "inloggning"],
    },
    {
        id: "mejl-till-alla-eller-grupp",
        minutes: 5,
        sectionId: "communication",
        question: "Hur skickar jag ett mejl till alla medlemmar, eller bara till en grupp?",
        intro: "Utskick görs under Kommunikation i två steg: först väljer du mottagare, sedan skriver du meddelandet.",
        steps: [
            "Gå till [[Kommunikation > Skicka e-post > Grupp/kategori]].",
            "Välj mottagare i steg 1: en eller flera grupper, eller lämna fältet tomt för att skicka till alla medlemmar. Du kan också avgränsa på medlemsavgift och betalstatus.",
            "Klicka på \"Skapa sändlista\" och kontrollera listan över mottagare.",
            "Skriv ämne och meddelande i steg 2, eller välj \"Utgå från e-postmall\". Du kan bifoga filer.",
            "Klicka på Förhandsgranska, och Testskicka gärna till dig själv först.",
            "Klicka på \"Skicka e-post\" och bekräfta.",
        ],
        notes: [
            "Medlemmar som har valt att inte få e-post hoppas över. Gäller det viktig information, till exempel kallelse till årsmöte, kan du kryssa i \"Skicka till medlemmar som inte önskar e-post\".",
            "Ska mailet bara till en person väljer du \"Enskild medlem\" i stället, och \"Externt\" för mottagare som inte är medlemmar.",
            "Utskick till grupper kräver ett betalabonnemang.",
        ],
        links: [
            { label: "Skicka e-post", path: "sendEmail" },
        ],
        keywords: ["mail", "e-post", "epost", "utskick", "nyhetsbrev", "massutskick", "alla", "grupp", "kategori", "skicka", "kallelse"],
    },
    {
        id: "valkomstmail-ny-medlem",
        minutes: 5,
        sectionId: "communication",
        question: "När en ny medlem registrerar sig vill jag skicka ett välkomstmail, hur gör jag?",
        intro: "Skapa ett automatiskt utskick för händelsen ny medlem. Mailet skickas av sig självt när medlemmen har registrerat sig och aktiverat sitt konto.",
        steps: [
            "Gå till [[Inställningar > Epostmallar]] och öppna fliken Automatiska utskick.",
            "Klicka på \"Skapa automatiskt utskick\" och välj händelsen \"Välkomstmail till ny medlem som registrerat sig\".",
            "Ge utskicket ett namn och skriv ämne och text. Med variablerna i editorn kan du till exempel hälsa medlemmen välkommen med namn.",
            "Kryssa i \"Använd designmall\" om mailet ska ha en bild överst eller en färgad rubrik.",
            "Klicka på \"Skapa automatiskt utskick\" längst ner. Från och med nu får varje ny medlem mailet automatiskt.",
        ],
        notes: [
            "Vill du själv få veta när någon har blivit medlem? Skapa även ett utskick med händelsen \"Notifiering till administratör när en ny medlem har registrerat sig och aktiverat sitt konto\" och ange vilken e-postadress som ska få det.",
            "Mailet skickas bara till medlemmar som registrerar sig själva via er medlemssida, inte till medlemmar som en administratör lägger upp.",
        ],
        links: [
            { label: "Öppna Epostmallar", path: "emailTemplatesAndAutomation" },
        ],
        keywords: ["välkommen", "välkomstbrev", "automatiskt", "automatiska utskick", "händelsestyrt", "nyregistrering", "mall", "notifiering", "mejl", "mail"],
    },
    {
        id: "egen-doman-epost",
        minutes: 15,
        sectionId: "communication",
        question: "Jag vill skicka e-post från min egen domän, hur gör jag?",
        intro: "Du lägger till er domän i OneFront 360 och publicerar några DNS-poster hos er domänleverantör. Då skickas utskicken från er egen adress och hamnar mer sällan i skräpposten.",
        steps: [
            "Gå till [[Inställningar > Konto]] och kontrollera att Organisationens e-postadress är ifylld under Organisationsinställningar. Det är den adressen utskicken skickas från.",
            "Gå till avsnittet Domän/E-post inställningar längre ner på samma sida, skriv in er domän och klicka på \"Lägg till domän\".",
            "Logga in hos er domänleverantör (t.ex. Loopia eller Telia) och lägg in DNS-posterna som visas, bland annat för DKIM och DMARC. Kopiera namn, typ och värde exakt.",
            "Vänta tills ändringen har slagit igenom. Det kan ta från några minuter upp till 24 timmar.",
            "Gå tillbaka till samma sida och klicka på \"Autentisera domän\".",
        ],
        notes: [
            "Avsnittet Domän/E-post inställningar visas bara för administratörer med fullständig behörighet, och först när organisationens e-postadress är sparad.",
            "Det måste vara en domän ni äger. Adresser hos till exempel Gmail eller Outlook går inte att använda.",
            "Misslyckas autentiseringen? Vänta en stund och prova igen, eller be er webbhotellssupport kontrollera posterna. På sidan finns också en hjälpruta med instruktioner för Loopia och Telia.",
        ],
        links: [
            { label: "Öppna Inställningar", path: "settings" },
        ],
        keywords: ["domän", "avsändare", "avsändaradress", "dkim", "dmarc", "dns", "skräppost", "spam", "loopia", "telia", "mejl", "mail"],
    },
    {
        id: "epost-kom-inte-fram",
        minutes: 2,
        sectionId: "communication",
        question: "Varför fick min medlem inte e-postmeddelandet?",
        intro: "Oftast har meddelandet hamnat i mottagarens skräppost. Hur det sorteras beror på vilken e-postleverantör mottagaren har.",
        steps: [
            "Be medlemmen titta i skräpposten och markera meddelandet som \"inte skräppost\".",
            "Kontrollera under [[Kommunikation > E-post/sms uppföljning]] om meddelandet har levererats eller studsat.",
            "Kontrollera att medlemmens e-postadress är rättstavad.",
        ],
        notes: [
            "Kopplar ni er egen domän till OneFront 360 signeras utskicken med er avsändaradress, vilket minskar risken att de hamnar i skräpposten.",
        ],
        links: [
            { label: "Öppna E-post/sms uppföljning", path: "emailFollowUp" },
        ],
        keywords: ["mejl", "mail", "skräppost", "spam", "inbjudan", "levererat", "studs", "domän"],
    },
    {
        id: "bjuda-in-till-arsmote",
        minutes: 10,
        sectionId: "events",
        question: "Hur bjuder jag in medlemmarna till årsmötet och ser vilka som kommer?",
        intro: "Skapa ett event och skicka inbjudan till en grupp. Varje medlem får en personlig länk att svara på, och du ser svaren samlade på eventet.",
        steps: [
            "Gå till Events och klicka på \"Skapa nytt event\".",
            "Fyll i namn, beskrivning, plats och datum. Välj vilka svar som ska gå att ge, till exempel Ja / Nej.",
            "Sätt en deadline om du behöver veta antalet i god tid, och slå på automatisk påminnelse om du vill.",
            "Klicka på \"Skapa event\".",
            "Öppna eventet, välj Inbjudningar i menyn till vänster, klicka på \"Skicka inbjudan\" och välj \"Skicka till grupp\".",
            "Välj grupp, klicka på \"Skapa sändlista\", skriv ett meddelande och klicka på \"Skicka inbjudan\".",
            "Följ svaren under \"Status & Svar\" på eventet. Listan med svar går att ladda ner.",
        ],
        notes: [
            "På sidan Inbjudningar finns också avsnittet Påminnelser. Där ser du hur många som inte har svarat och kan skicka en påminnelse bara till dem.",
            "Du kan också bjuda in enskilda medlemmar eller personer utanför föreningen via samma knapp.",
            "Deadline, automatisk påminnelse och inbjudan till grupper kräver ett betalabonnemang.",
        ],
        links: [
            { label: "Öppna Events", path: "myevents" },
        ],
        keywords: ["event", "evenemang", "årsmöte", "möte", "inbjudan", "bjuda in", "anmälan", "osa", "deltagare", "svar", "kallelse", "aktivitet"],
    },
    {
        id: "spara-dokument",
        minutes: 2,
        sectionId: "files",
        question: "Var kan styrelsen spara dokument som protokoll och stadgar?",
        intro: "Under Dokument kan ni ladda upp föreningens filer och sortera dem i mappar. Dokumenten syns bara för administratörer, inte för medlemmarna.",
        steps: [
            "Gå till [[Inställningar > Dokument]].",
            "Klicka på \"Ladda upp dokument\" och välj filen på din dator.",
            "Välj en befintlig mapp eller skriv namnet på en ny, till exempel \"Protokoll 2026\". Du kan också lämna mappen tom.",
            "Klicka på OK. Filen syns nu i listan, där den går att ladda ner, flytta till en annan mapp eller ta bort.",
        ],
        notes: [
            "Du kan ladda upp PDF, Word, Excel, JPG och PNG, högst 25 MB per fil.",
            "Laddar du upp en fil med samma namn som en som redan finns får du frågan om den ska skrivas över.",
            "Ska bilden användas i ett utskick? Ladda upp den i bildbanken i stället, se området Bildbank.",
            "Dokument kräver abonnemanget Premium eller högre. Menyvalet visas inte annars.",
        ],
        links: [
            { label: "Öppna Dokument", path: "documents" },
        ],
        keywords: ["fillagring", "filer", "fil", "dokument", "ladda upp", "spara", "lagra", "mapp", "protokoll", "stadgar", "verksamhetsberättelse", "pdf", "word", "excel", "arkiv"],
    },
    {
        id: "bildbank-ladda-upp",
        minutes: 3,
        sectionId: "images",
        question: "Hur laddar jag upp bilder och använder dem i utskick?",
        intro: "Ladda upp bilderna i bildarkivet. Där går de att återanvända i e-post, e-postmallar och som bild på event.",
        steps: [
            "Gå till [[Kommunikation > Bildarkiv]].",
            "Klicka på \"Ladda upp bild\" och välj bilden på din dator.",
            "När du skriver ett e-postmeddelande eller en mall klickar du på bildknappen för bildarkivet i verktygsfältet och väljer bilden.",
        ],
        notes: [
            "Bilderna ska vara JPG eller PNG och mindre än 1 MB. Bildarkivet rymmer totalt 20 MB.",
            "Du kan också ladda upp en ny bild direkt från editorn. Den sparas då i bildarkivet.",
            "Bilderna i arkivet är publika, så att de kan visas direkt i mailet. Ladda inte upp bilder som inte får spridas.",
            "Tar du bort en bild försvinner den även ur mail som redan har skickats och från event där den används.",
            "Bildarkivet ingår inte i gratisversionen.",
        ],
        links: [
            { label: "Öppna Bildarkiv", path: "picturearchive" },
        ],
        keywords: ["bildbank", "bildarkiv", "bild", "bilder", "foto", "logga", "logotyp", "ladda upp", "jpg", "png", "nyhetsbrev", "mall", "eventbild"],
    },
];
